import { app, Component, ojs, state, v } from "modular-openscriptjs";
import { dom } from "../../../fotastart";
import { pageInfo, scrollToTop, inform, either } from "../../../helpers";
import { api, STRINGS } from "../../../declarations";

const h = app("h");
const router = app("router");

export default class AddProductsFromImages extends Component {
    companyId = null;
    images = state([]);
    isUploading = state(false);

    render() {
        this.companyId = router.params.companyId || window.currentCompany?.id;
        pageInfo("Add Products from Images");
        scrollToTop();

        return h.div(
            { class: "min-vh-100 py-5 bg-light mb-5 py-md-5" },
            h.div(
                { class: "container" },
                h.div(
                    { class: "mb-5 d-flex align-items-center gap-3" },
                    h.button(
                        {
                            class: "btn btn-white border shadow-sm circle p-2",
                            listeners: {
                                click: () => history.back(),
                            },
                        },
                        h.i({ class: "bi bi-arrow-left" }),
                    ),
                    h.h2({ class: "fw-bold mb-0" }, "Add Products from Images"),
                ),

                h.div(
                    { class: "row justify-content-center" },
                    h.div(
                        { class: "col-lg-8" },
                        h.div(
                            { class: "card border-0 shadow-sm p-4 p-md-5" },
                            h.div(
                                { class: "mb-4 text-center" },
                                h.h4({ class: "fw-bold" }, "Upload Images"),
                                h.p(
                                    { class: "text-muted" },
                                    "Take a picture of your products, shelves, or menus, or select from your gallery. Our AI will automatically extract and list the products for you.",
                                ),
                            ),

                            h.div(
                                {
                                    class: "border-2 border-dashed rounded-4 p-5 text-center bg-light cursor-pointer hover-shadow transition-all mb-4",
                                    style: "border-color: #dee2e6;",
                                    listeners: {
                                        click: () =>
                                            dom
                                                .id("image-upload-input")
                                                .click(),
                                        dragover: (e) => e.preventDefault(),
                                        drop: (e) => this.handleDrop(e),
                                    },
                                },
                                h.i({
                                    class: "bi bi-images display-1 text-primary mb-3 d-block",
                                }),
                                h.h5(
                                    { class: "fw-bold" },
                                    "Click or Drag to Upload",
                                ),
                                h.p(
                                    { class: "text-muted small mb-0" },
                                    "Supports JPG, PNG. You can also take a picture directly on your mobile device.",
                                ),
                            ),
                            h.input({
                                type: "file",
                                accept: "image/*",
                                multiple: "multiple",
                                class: "d-none",
                                id: "image-upload-input",
                                capture: "environment",
                                listeners: {
                                    change: (e) => this.handleFileChange(e),
                                },
                            }),

                            v(this.images, (imgs) => {
                                if (imgs.value.length === 0) return null;
                                return h.div(
                                    { class: "mb-4" },
                                    h.h6(
                                        { class: "fw-bold mb-3" },
                                        `Selected Images (${imgs.value.length})`,
                                    ),
                                    h.div(
                                        { class: "row g-3" },
                                        ...imgs.value.map((img, idx) =>
                                            h.div(
                                                { class: "col-4 col-md-3" },
                                                h.div(
                                                    {
                                                        class: "position-relative border rounded-3 overflow-hidden bg-light",
                                                        style: "padding-top: 100%;",
                                                    },
                                                    h.img({
                                                        src: img.url,
                                                        class: "position-absolute top-0 start-0 w-100 h-100",
                                                        style: "object-fit: cover;",
                                                    }),
                                                    h.button(
                                                        {
                                                            class: "btn btn-danger btn-sm rounded-circle position-absolute",
                                                            style: "top: 5px; right: 5px; width: 24px; height: 24px; padding: 0; display: flex; align-items: center; justify-content: center;",
                                                            listeners: {
                                                                click: (e) => {
                                                                    e.stopPropagation();
                                                                    this.removeImage(
                                                                        idx,
                                                                    );
                                                                },
                                                            },
                                                        },
                                                        h.i({
                                                            class: "bi bi-x-lg",
                                                            style: "font-size: 12px;",
                                                        }),
                                                    ),
                                                ),
                                            ),
                                        ),
                                    ),
                                );
                            }),

                            v(this.isUploading, (uploading) => {
                                return h.button(
                                    {
                                        class: "btn btn-primary w-100 py-3 rounded-pill fw-bold d-flex align-items-center justify-content-center",
                                        listeners: {
                                            click: () => this.submit(),
                                        },
                                    },
                                    either(uploading.value, { disabled: true }),
                                    uploading.value
                                        ? h.span({
                                              class: "spinner-border spinner-border-sm me-2",
                                          })
                                        : h.i({ class: "bi bi-stars me-2" }),
                                    uploading.value
                                        ? "Processing with AI..."
                                        : "Extract Products",
                                );
                            }),
                        ),
                    ),
                ),
            ),
        );
    }

    handleFileChange(e) {
        this.addFiles(e.target.files);
        e.target.value = "";
    }

    handleDrop(e) {
        e.preventDefault();
        this.addFiles(e.dataTransfer.files);
    }

    async addFiles(files) {
        if (!files || files.length === 0) return;

        const current = this.images.value || [];
        const incomingFiles = Array.from(files).filter((f) =>
            f.type.startsWith("image/"),
        );

        const newImages = [];

        for (const file of incomingFiles) {
            // 1. Force the file to read its binary array buffer immediately into memory
            const arrayBuffer = await file.arrayBuffer();

            // 2. Re-instantiate it as a clean, self-contained Web Blob
            const robustBlob = new Blob([arrayBuffer], { type: file.type });

            newImages.push({
                // Save the concrete, standalone blob instead of the fragile File instance
                file: robustBlob,
                name: file.name,
                url: URL.createObjectURL(robustBlob),
            });
        }

        this.images.value = [...current, ...newImages];
    }

    removeImage(idx) {
        const current = [...this.images.value];
        URL.revokeObjectURL(current[idx].url);
        current.splice(idx, 1);
        this.images.value = current;
    }

    async submit() {
        if (this.images.value.length === 0) return;

        this.isUploading.value = true;

        try {
            const payload = new FormData();

            if (this.images.value) {
                for (let i = 0; i < this.images.value.length; i++) {
                    let img = this.images.value[i];
                    payload.append("images[]", img.file);
                }
            }

            let res = await api.post(
                `/companies/${this.companyId}/products/extract-from-images`,
                payload,
                {
                    fetch_using: "browser",
                    "remove-content-type": true,
                },
            );

            if (!res) {
                return;
            }

            inform(
                "Images uploaded successfully! Our AI is working in the background to extract and create your products.",
                STRINGS.SUCCESS,
                5000,
            );

            this.images.value.forEach((img) => URL.revokeObjectURL(img.url));
            this.images.value = [];

            router.to("business.manage.inventory", {
                companyId: this.companyId,
            });
        } catch (error) {
            inform(
                error.response?.data?.message || "Failed to upload images.",
                STRINGS.ERROR,
            );
        } finally {
            this.isUploading.value = false;
        }
    }
}

ojs(AddProductsFromImages);
